<script>
  import {
    useOutsideClick,
  } from '@/helpers';
  import DatePicker from 'vuejs-datepicker';
  import ArrowDown from '@/assets/arrow-down.svg';
  import { ptBR } from 'vuejs-datepicker/dist/locale';

  const have_modal = ['select', 'date'];

  const formatDate = date => date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    year: '2-digit',
    month: '2-digit',
  });

  export default {
    components: {
      ArrowDown,
      DatePicker,
    },

    inheritAttrs: false,

    props: {
      icon: {
        type: Object,
        required: false,
      },

      type: {
        type: String,
        required: true,
      },

      label: {
        type: String,
        required: true,
      },

      value: {
        required: false,
      },

      options: {
        type: Array,
        required: false,
        default: () => ([
          //
        ]),
      },

      allowed_dates: {
        type: Array,
        required: false,
        default: () => ([
          //
        ]),
      },
    },

    data: () => ({
      show: false,
    }),

    mounted () {
      if (! have_modal.includes(this.type)) {
        return;
      }

      useOutsideClick(this, 'input', () => this.show = false);
    },

  computed: {
      placeholder () {
        if (this.type == 'select') {
          const selected = this.options.find(({ value }) => value === this.value);

          if (selected) {
            return selected.label;
          }
        }

        if (this.type == 'date') {
          const range = this.value || {};

          if (range.from) {
            const from = formatDate(new Date(range.from));

            if (range.to) {
              const to = formatDate(new Date(range.to));

              return `${from} - ${to}`;
            }

            return from;
          }
        }

        return this.label;
      },

      is_placeholder () {
        return have_modal.includes(this.type);
      },

      date_picker () {
        const range = this.value || {};

        return {
          inline: true,
          language: ptBR,
          maximumView: 'day',
          disabledDates: {
            // @todo fix years months
            customPredictor: date => ! this.allowed_dates.includes(date.setHours(0, 0, 0, 0)),
          },

          highlighted: {
            from: range.from,
            to: range.to,
          },
        };
      },
    },

    methods: {
      select (value) {
        this.show = false;

        this.$emit('input', value);
      },

      pickDate (date) {
        const {
          to,
          from,
        } = this.value || {};
        const zeroed = date.setHours(0, 0, 0, 0);

        if (to && ! from) {
          const gt = zeroed > to;

          this.$emit('input', {
            to: gt ? zeroed : to,
            from: gt ? to : zeroed,
          });

          this.show = false;

          return;
        }

        this.$emit('input', {
          to: zeroed,
        });
      },
    },
  };
</script>

<template>
  <div
    ref="input"
    class="financier-input"
    @click.prevent="show = ! show"
    >
    <template
      v-if="is_placeholder"
      >

      <input
        readonly
        type="text"
        v-bind="$attrs"
        autocomplete="off"
        :value="placeholder"
        class="financier-input__control financier-input__control--placeholder"
        >

        <transition name="fade-up">
          <ul
            v-if="show && type === 'select'"
            class="financier-input__list"
            >

            <li
              :key="option.value"
              class="financier-input__item"
              v-for="option in options"
              >

              <a
                href="#"
                v-text="option.label"
                class="financier-input__item-link"
                @click.prevent.stop="select(option.value)"
                :class="{ 'financier-input__item-link--active': value === option.value }"
                >
              </a>
            </li>
          </ul>

          <div
            @click.prevent.stop
            class="financier-input__date"
            v-else-if="show && type === 'date'"
            >
            <DatePicker
              v-bind="date_picker"
              @selected="pickDate"
              />
          </div>
        </transition>
    </template>

    <template v-else>
      <input
        :type="type"
        :value="value"
        v-bind="$attrs"
        :placeholder="label"
        class="financier-input__control"
        @input="$emit('input', $event.target.value)"
        >
    </template>

    <span
      class="financier-input__icon"
      :class="{ 'financier-input__icon--flipped': show && type === 'select' }"
      >

      <Component
        :is="icon ? icon : 'ArrowDown'"
        />
    </span>
  </div>
</template>

<style lang="scss">
  .financier-input {
    width: auto;
    position: relative;

    * {
      transition: 250ms;
    }

    &__control {
      display: flex;
      color: #FFF;
      max-width: 168px;
      background: none;
      line-height: 1.25;
      border-radius: 4px;
      align-items: center;
      letter-spacing: .32px;
      padding: 12px 40px 12px 16px;
      border: 1px solid rgba(#FFF, .8);

      &:hover,
      &:focus {
        border-color: #FFF;
        background-color: rgba(#FFF, .15);
      }

      &::placeholder {
        color: #b2b8c5;
      }

      &--placeholder {
        cursor: pointer;
      }
    }

    &__list,
    &__date {
      left: 0;
      top: 56px;
      width: 210px;
      font-size: 14px;
      line-height: 1.14;
      position: absolute;
      color: rgba(#FFF, .8);
    }

    &__list {
      padding: 12px 0;
      border-radius: 4px;
      background-color: #14162f;
    }

    &__date .vdp-datepicker {
      &__calendar {
        border: 0;
        width: 300px;
        padding: 9px;
        border-radius: 4px;
        background-color: #14162f;

        header span {
          padding: 8px 0;
        }

        .next::after {
          border-left-color: rgba(#FFF, .8);
        }

        .prev::after {
          border-right-color: rgba(#FFF, .8);
        }

        .next,
        .prev {
          border-radius: 4px;

          &:hover {
            background-color: rgba(#FFF, .1) !important;
          }
        }

        .day__month_btn {
          user-select: none;
          background: transparent !important;
        }

        .cell {
          padding: 0;
          margin: 3px;
          height: 30px;
          width: 12.162%;
          line-height: 30px;
          border-radius: 4px;
          border: 0 !important;

          &.day {
          }

          &.day-header {
            user-select: none;
            background: transparent !important;
          }

          &.selected,
          &.highlighted {
            background-color: #3e388b !important;
          }

          &.disabled {
            opacity: .5;
          }

          &:not(.blank):not(.disabled):not(.selected):not(.highlighted):hover {
            background-color: rgba(#FFF, .1);
          }
        }
      }
    }

    &__item {
      &-link {
        display: block;
        padding: 12px 16px;

        &:hover {
          background-color: rgba(#FFF, .1);
        }

        &--active {
          background-color: #3e388b !important;
        }
      }

      &:first-of-type &-link {
        border-radius: 4px 4px 0  0;
      }

      &:last-of-type &-link {
        border-radius: 0 0 4px 4px;
      }
    }

    &__icon {
      top: 0;
      bottom: 0;
      color: #FFF;
      right: 16px;
      display: flex;
      position: absolute;
      align-items: center;
      justify-content: center;

      &--flipped {
        transform: rotate(180deg);
      }
    }
  }
</style>
